import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Paper, Popover } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { Check, Circle, Trash2, UserRound, X } from 'lucide-react';
import { AlineButton, ConfirmationDialog } from 'src/components';
import type { UserOption } from 'src/components/ai/reminder-automation/UserAutocomplete';
import { UserAutocomplete } from 'src/components/ai/reminder-automation/UserAutocomplete';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack } from 'src/helpers';
import {
  useAcceptRemindersWithCacheUpdate,
  useDeleteReminders,
  useGetSelectedRowIds,
  useRejectRemindersWithCacheUpdate,
  useUpdateReminders,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import type { ReminderListRow } from './ColumnDef';
import { CompleteReminderIcon } from './CompleteReminderIcon';
import { useRemindersContext } from './RemindersContext';

const useStyles = makeStyles()(theme => ({
  assignButton: {
    padding: theme.spacing(0, 1, 1, 1),
  },
  assigneesPopover: {
    transform: 'translateY(-4px)',
  },
  button: {
    borderColor: theme.palette.grey[300],
  },
  wrapper: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderRadius: 100,
    bottom: 16,
    display: 'flex',
    gap: theme.spacing(1),
    left: '50%',
    padding: theme.spacing(1),
    position: 'fixed',
    transform: 'translateX(-50%)',
    width: 'fit-content',
    zIndex: theme.zIndex.appBar,
  },
}));

export const RemindersFloatingActionBar = React.memo(function RemindersFloatingActionBar() {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const barRef = useRef<HTMLDivElement>(null);
  const { remindersState } = useRemindersContext();
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignees, setAssignees] = useState<UserOption[]>([]);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { selectedRowIds: selectedReminderIds } = useGetSelectedRowIds();
  const { loading: updatingReminders, updateReminders } = useUpdateReminders();
  const { deleteReminders, loading: deletingReminders } = useDeleteReminders();
  const { acceptReminders, loading: acceptingReminders } = useAcceptRemindersWithCacheUpdate();
  const { loading: rejectingReminders, rejectReminders } = useRejectRemindersWithCacheUpdate();

  const dispatchAcceptSuccessSnack = useDispatchSuccessSnack({
    message: 'Reminders accepted successfully.',
  });
  const dispatchRejectSuccessSnack = useDispatchSuccessSnack({
    message: 'Reminders rejected successfully.',
  });
  const dispatchAssignSuccessSnack = useDispatchSuccessSnack({
    message: 'Reminders assigned successfully.',
  });
  const dispatchCompleteSuccessSnack = useDispatchSuccessSnack({
    message: 'Reminders marked as complete.',
  });
  const dispatchReopenSuccessSnack = useDispatchSuccessSnack({
    message: 'Reminders reopened.',
  });
  const dispatchDeleteSuccessSnack = useDispatchSuccessSnack({
    message: 'Reminders deleted successfully.',
  });

  // Analyze selected reminders to determine which actions are available
  const selectionInfo = useMemo(() => {
    const rows = selectedReminderIds
      .map(id => apiRef.current.getRow(id))
      .filter(
        (row): row is ReminderListRow => row !== null && typeof row === 'object' && 'id' in row
      );

    const pendingReviewCount = rows.filter(
      r => r.reviewStatus === Apollo.ReminderReviewStatus.PendingReview
    ).length;
    const acceptedCount = rows.length - pendingReviewCount;
    const completedCount = rows.filter(r => Boolean(r.completedAt)).length;

    return {
      allAccepted: acceptedCount === rows.length && rows.length > 0,
      allCompleted: completedCount === rows.length && rows.length > 0,
      allPendingReview: pendingReviewCount === rows.length && rows.length > 0,
      anyAccepted: acceptedCount > 0,
      anyPendingReview: pendingReviewCount > 0,
      pendingReviewIds: rows
        .filter(r => r.reviewStatus === Apollo.ReminderReviewStatus.PendingReview)
        .map(r => r.id),
      total: rows.length,
    };
  }, [selectedReminderIds, apiRef]);

  const clearSelection = useCallback(() => {
    apiRef.current.setRowSelectionModel({ ids: new Set(), type: 'include' });
  }, [apiRef]);

  const handleAcceptAll = useCallback(async () => {
    const idsToAccept = selectionInfo.pendingReviewIds;
    if (!idsToAccept.length) return;
    await acceptReminders({ variables: { reminderIds: idsToAccept } });
    dispatchAcceptSuccessSnack();
    clearSelection();
  }, [selectionInfo.pendingReviewIds, acceptReminders, dispatchAcceptSuccessSnack, clearSelection]);

  const handleRejectAll = useCallback(async () => {
    const idsToReject = selectionInfo.pendingReviewIds;
    if (!idsToReject.length) return;
    await rejectReminders({ variables: { reminderIds: idsToReject } });
    dispatchRejectSuccessSnack();
    clearSelection();
  }, [selectionInfo.pendingReviewIds, rejectReminders, dispatchRejectSuccessSnack, clearSelection]);

  const handleAssigneeChange = useCallback((users: UserOption[]) => {
    setAssignees(users);
  }, []);

  const handleAssignConfirm = useCallback(async () => {
    if (!assignees.length) return;
    const assigneeIds = assignees.map(u => u.id);
    await updateReminders({
      variables: {
        inputs: selectedReminderIds.map(reminderId => ({
          assigneeIds,
          reminderId,
        })),
      },
    });
    dispatchAssignSuccessSnack();
    setAssignOpen(false);
    setAssignees([]);
    clearSelection();
  }, [assignees, selectedReminderIds, updateReminders, dispatchAssignSuccessSnack, clearSelection]);

  const handleAssignPopoverClose = useCallback(() => {
    setAssignOpen(false);
    setAssignees([]);
  }, []);

  const handleCompleteAll = useCallback(async () => {
    const idsToComplete = selectedReminderIds.filter(
      id => !selectionInfo.pendingReviewIds.includes(id)
    );
    if (!idsToComplete.length) return;

    await updateReminders({
      variables: {
        inputs: idsToComplete.map(reminderId => ({
          props: { completedAt: new Date().toISOString() },
          reminderId,
        })),
      },
    });
    dispatchCompleteSuccessSnack();
    clearSelection();
  }, [
    selectedReminderIds,
    selectionInfo.pendingReviewIds,
    updateReminders,
    dispatchCompleteSuccessSnack,
    clearSelection,
  ]);

  const handleReopenAll = useCallback(async () => {
    await updateReminders({
      variables: {
        inputs: selectedReminderIds.map(reminderId => ({
          props: { completedAt: null },
          reminderId,
        })),
      },
    });
    dispatchReopenSuccessSnack();
    clearSelection();
  }, [selectedReminderIds, updateReminders, dispatchReopenSuccessSnack, clearSelection]);

  const handleDeleteAll = useCallback(async () => {
    const idsToDelete = selectedReminderIds.filter(
      id => !selectionInfo.pendingReviewIds.includes(id)
    );
    if (!idsToDelete.length) return;

    await deleteReminders({ variables: { reminderIds: idsToDelete } });
    dispatchDeleteSuccessSnack();
    clearSelection();
  }, [
    selectedReminderIds,
    selectionInfo.pendingReviewIds,
    deleteReminders,
    dispatchDeleteSuccessSnack,
    clearSelection,
  ]);

  // Show when items are selected
  if (!selectedReminderIds.length) return null;

  const isLoading =
    acceptingReminders || rejectingReminders || updatingReminders || deletingReminders;
  const isMixedSelection = selectionInfo.anyPendingReview && selectionInfo.anyAccepted;
  const showCompleted = remindersState.filter === Apollo.ReminderFilter.Completed;

  return (
    <Paper
      ref={barRef}
      className={classes.wrapper}
      elevation={2}
      onClick={e => e.stopPropagation()}
    >
      {/* Accept/Reject: only show when pending review reminders are selected */}
      {selectionInfo.anyPendingReview && (
        <>
          <AlineButton
            buttonClassName={classes.button}
            disabled={isLoading}
            startIcon={<Check size={18} />}
            variant="outlined"
            onClick={handleAcceptAll}
          >
            Accept{isMixedSelection ? ` (${selectionInfo.pendingReviewIds.length})` : ''}
          </AlineButton>
          <AlineButton
            buttonClassName={classes.button}
            disabled={isLoading}
            startIcon={<X size={18} />}
            variant="outlined"
            onClick={() => setRejectConfirmOpen(true)}
          >
            Reject{isMixedSelection ? ` (${selectionInfo.pendingReviewIds.length})` : ''}
          </AlineButton>
        </>
      )}

      {/* Complete/Reopen: only show for accepted reminders */}
      {selectionInfo.anyAccepted && !showCompleted && (
        <AlineButton
          buttonClassName={classes.button}
          disabled={isLoading}
          startIcon={<CompleteReminderIcon size={18} />}
          variant="outlined"
          onClick={handleCompleteAll}
        >
          Complete
          {isMixedSelection
            ? ` (${selectionInfo.total - selectionInfo.pendingReviewIds.length})`
            : ''}
        </AlineButton>
      )}

      {selectionInfo.anyAccepted && showCompleted && (
        <AlineButton
          buttonClassName={classes.button}
          disabled={isLoading}
          startIcon={<Circle size={18} />}
          variant="outlined"
          onClick={handleReopenAll}
        >
          Reopen
        </AlineButton>
      )}

      {/* Assign: always available */}
      <AlineButton
        buttonClassName={classes.button}
        disabled={isLoading}
        startIcon={<UserRound size={18} />}
        variant="outlined"
        onClick={() => setAssignOpen(true)}
      >
        Assign
      </AlineButton>
      <Popover
        anchorEl={barRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        className={classes.assigneesPopover}
        open={assignOpen}
        slotProps={{
          paper: {
            elevation: 0,
            sx: theme => ({
              border: `1px solid ${theme.appColors.rule}`,
              borderRadius: 3,
              overflow: 'hidden',
              width: 280,
            }),
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={handleAssignPopoverClose}
      >
        <UserAutocomplete
          alwaysOpen
          autoFocus
          disablePortal
          maxChips={4}
          placeholder="Select assignees..."
          value={assignees}
          onChange={handleAssigneeChange}
        />
        <AlineButton
          fullWidth
          className={classes.assignButton}
          color="primary"
          disabled={!assignees.length || isLoading}
          loading={updatingReminders}
          variant="contained"
          onClick={handleAssignConfirm}
        >
          {`Assign ${selectedReminderIds.length} Reminder${selectedReminderIds.length === 1 ? '' : 's'}`}
        </AlineButton>
      </Popover>

      {/* Delete: only for accepted reminders */}
      {selectionInfo.anyAccepted && (
        <AlineButton
          buttonClassName={classes.button}
          disabled={isLoading}
          startIcon={<Trash2 size={18} />}
          variant="outlined"
          onClick={() => setDeleteConfirmOpen(true)}
        >
          Delete
          {isMixedSelection
            ? ` (${selectionInfo.total - selectionInfo.pendingReviewIds.length})`
            : ''}
        </AlineButton>
      )}
      <ConfirmationDialog
        message={`Are you sure you want to reject ${selectionInfo.pendingReviewIds.length === 1 ? 'this reminder' : 'the selected reminders'}? This action cannot be undone.`}
        open={rejectConfirmOpen}
        primaryText="Reject"
        title={`Reject ${selectionInfo.pendingReviewIds.length === 1 ? 'reminder' : 'reminders'}?`}
        onClickPrimary={async () => {
          await handleRejectAll();
          setRejectConfirmOpen(false);
        }}
        onClose={() => setRejectConfirmOpen(false)}
      />
      <ConfirmationDialog
        message={`Are you sure you want to delete ${selectedReminderIds.length === 1 ? 'this reminder' : 'the selected reminders'}? This action cannot be undone.`}
        open={deleteConfirmOpen}
        title={`Delete ${selectedReminderIds.length === 1 ? 'reminder' : 'reminders'}?`}
        onClickPrimary={async () => {
          await handleDeleteAll();
          setDeleteConfirmOpen(false);
        }}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </Paper>
  );
});
